import { useState } from "react";

import type { RapidDataType } from "@rapid/store/slices/rapidDataSlice";
import { useToast } from "@rapid/hooks/useToast";
import { ModalWithTitle } from "@scripts/react/components/RfModal/ModalWithTitle";
import TextInput from "@scripts/react/components/TextInput";

type Props = {
    isOpen: boolean;
    onClose: () => void;
    modelToDelete: RapidDataType;
    onConfirm: () => void;
};

export function DeleteModelModal({ isOpen, onClose, modelToDelete, onConfirm }: Props) {
    const [isLoading, setIsLoading] = useState(false);
    const [confirmText, setConfirmText] = useState("");
    const toast = useToast();

    const onSubmit = async () => {
        setIsLoading(true);
        try {
            await onConfirm();
            onClose();
            toast.success("Model deleted successfully.");
        } catch (error) {
            console.error("Error deleting model:", error);
            toast.error("Error deleting model. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <ModalWithTitle
            isOpen={isOpen}
            onClose={onClose}
            type="danger"
            icon="far fa-trash-alt"
            title="Delete Model"
            headerClassName="text-destructive"
            cancelButtonText="Cancel"
            submitButtonText="Delete Model"
            submitButtonProps={{
                disabled: confirmText !== modelToDelete.workflowUrl || isLoading,
                loading: isLoading,
                loadingText: "Deleting..."
            }}
            onSubmit={onSubmit}
        >
            <span className="text-base">
                To delete this project, please confirm the model ID:
                <br />
                <code className="text-destructive">{modelToDelete.workflowUrl}</code>
            </span>
            <TextInput
                size="medium"
                label="Model ID"
                placeholder={modelToDelete.workflowUrl}
                fullWidth={true}
                value={confirmText}
                onChange={(v) => setConfirmText(v)}
            />
            <span className="font-semibold">
                This is a destructive action and cannot be undone. It will destroy all data
                associated with the model.
            </span>
        </ModalWithTitle>
    );
}
